import Footer from "components/Footers/Footer";
import Navbar from "components/Navbars/IndexNavbar";
import Swal from "sweetalert2";
import React, { useState, useEffect, useCallback } from "react";
import "react-block-ui/style.css";
import BASE_URL from "components/BaseUrl/apiConfig";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { SlidersHorizontal } from "lucide-react";

const UserExpenses = () => {
  //userExpenses
  const [loading, setLoading] = useState(false); // Add this line to define 'loading'
  const [currency, setCurrency] = useState("");
  const [expenseData, setExpenseData] = useState({
    expenseDescritions: [],
    expenses: [],
    totalDebited: 0,
    totalCreated: 0,
    availableBalance: "",
  });
  const [fixedData, setFixedData] = useState({
    expenseDescritions: [],
    expenses: [],
    totalDebited: 0,
    totalCreated: 0,
    availableBalance: "",
  });
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [years, setYears] = useState([]);
  const currentDate = new Date();
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const lastDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );
  const urlSearchParams = new URLSearchParams(window.location.search);
  const userKey = urlSearchParams.get("UserKey");

  // util: sort by date desc
  const sortByDateDesc = (list = []) => {
    return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  useEffect(() => {
    const currentYear = currentDate.getFullYear().toString();
    setSelectedYear(currentYear);
    fetchCurrencyData();
    fetchData(true);
  }, []); // Fetch data on component mount

  // extract years from fixedData
  useEffect(() => {
    if (fixedData && fixedData.expenses && fixedData.expenses.length > 0) {
      const uniqueYears = [
        ...new Set(
          fixedData.expenses.map((exp) => new Date(exp.date).getFullYear().toString())
        ),
      ].sort((a, b) => parseInt(b) - parseInt(a));
      setYears(uniqueYears);
    }
  }, [fixedData]);

  const fetchCurrencyData = async () => {
    try {
      const apirul1 = `${BASE_URL}/api/Users/GetCurrencyByGuiId1?guiId=${userKey}`;
      const response = await fetch(apirul1);
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await response.text(); // Parse response JSON
      setCurrency(data); // Assuming 'currency' is a property of the response
      console.log(data); // Log the response data
    } catch (error) {
      console.error("Error fetching currency data:", error);
    }
  };

  const fetchData = async (setAsFixed = false) => {
    try {
      setLoading(true);
      if (!userKey) {
        // Show an error message if UserKey is not present in the URL
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "UserKey not found in the URL.",
          showConfirmButton: true,
        });
        return;
      }
      const apiUrl = `${BASE_URL}/api/Expense/GetExpensesUserKey?UserKey=${userKey}`;
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const normalized = {
        ...data,
        expenses: sortByDateDesc(data.expenses || []),
      };
      if (setAsFixed) {
        setFixedData(normalized);
        setExpenseData(normalized);
      } else {
        setExpenseData(normalized);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Show an error message if there's an issue fetching data
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch expense data. Please try again later.",
        showConfirmButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // FRONTEND FILTER: apply filters to fixedData.expenses and update expenseData
  const applyFrontendFilter = useCallback(() => {
    const source = fixedData ? fixedData.expenses : [];
    if (!source.length) {
      setExpenseData((prev) => ({ ...prev, expenses: [] }));
      return;
    }
    let filtered = [...source];
    // Year filter
    if (selectedYear !== "All") {
      filtered = filtered.filter((item) => {
        const dt = new Date(item.date);
        return dt.getFullYear().toString() === selectedYear;
      });
    }
    // Month filter
    if (selectedMonth !== "All") {
      filtered = filtered.filter((item) => {
        const dt = new Date(item.date);
        // months stored as 1..12 in UI
        return (dt.getMonth() + 1).toString() === selectedMonth;
      });
    }
    // Date range filter
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const dt = new Date(item.date);
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return dt >= start && dt <= end;
      });
    }
    // always sort by date desc before setting
    const normalized = {
      ...fixedData,
      expenses: sortByDateDesc(filtered),
    };
    setExpenseData(normalized);
  }, [fixedData, selectedYear, selectedMonth, startDate, endDate]);

  // run frontend filter whenever selection changes or fixedData updates
  useEffect(() => {
    applyFrontendFilter();
  }, [applyFrontendFilter]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setSelectedMonth("All");
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const formatPayDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return ` ${day}/${month}/${year}`;
  };
  // Check if availableBalance is greater than 0
  const isPositiveBalance = expenseData.availableBalance > 0;
  // Set the color dynamically based on the condition
  const balanceColor = isPositiveBalance ? "rgb(0, 0, 255)" : "red";
  return (
    <>
      <Navbar />
      <div className="cust-div">
        <div className="container mx-auto mt-4 p-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl uppercase font-bold">
              Expenses(<span>{currency}</span>)
            </h1>
          </div>
          <div className="flex items-center mb-4 text-lg">
            <div className="flex-shrink-0">
              <span className="mr-4">
                <strong>Balance: </strong>
                <span style={{ color: balanceColor }}>
                  {expenseData.availableBalance}
                </span>{" "}
                |
              </span>
              <span className="mr-4">
                <strong>Credited:</strong>{" "}
                <span style={{ color: "rgb(50, 205, 50)" }}>
                  {expenseData.totalCreated}
                </span>{" "}
                |
              </span>
              <span className="mr-4">
                <strong>Debited:</strong>{" "}
                <span style={{ color: "orange" }}>
                  {expenseData.totalDebited}
                </span>
              </span>
            </div>
          </div>
          {/* ---------------- FILTER ROW (Year / Month / Date) ---------------- */}
          <div className="flex justify-end items-center gap-3 mb-4">
            {!showDateFilter && (
              <>
                {/* Year Dropdown */}
                <div className="relative" style={{ width: "100px" }}>
                  <select
                    value={selectedYear}
                    onChange={handleYearChange}
                    className="w-full px-3 py-2 appearance-none pr-8"
                    style={{ border: "1px solid #ddd", borderRadius: '5px' }}
                  >
                    <option value="All">All Years</option>
                    {years.map((year) => (
                      <option value={year} key={year}>{year}</option>
                    ))}
                  </select>
                </div>
                {/* Month Dropdown */}
                <div className="relative flex" style={{ width: "120px" }}>
                  <select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    className="w-full px-3 py-2 appearance-none pr-8"
                    style={{ border: "1px solid #ddd", borderRadius: '5px' }}
                  >
                    <option value="All">All Months</option>
                    {[
                      "January", "February", "March", "April", "May", "June",
                      "July", "August", "September", "October", "November", "December"
                    ].map((m, i) => (
                      <option value={i + 1} key={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {/* Date Picker */}
            {showDateFilter && (
              <DateRangePicker
                value={startDate && endDate ? [startDate, endDate] : null}
                onChange={(v) => {
                  if (!v) {
                    setStartDate(firstDayOfMonth);
                    setEndDate(lastDayOfMonth);
                    setSelectedYear(currentDate.getFullYear().toString());
                    setSelectedMonth((currentDate.getMonth() + 1).toString());
                    setShowDateFilter(false);
                  } else {
                    setStartDate(v[0]);
                    setEndDate(v[1]);
                    setSelectedYear("All");
                    setSelectedMonth("All");
                  }
                }}
                className="w-[240px]"
                format="dd/MM/yy"
                showOneCalendar
                character=" – "
              />
            )}
            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              className="p-2 rounded hover:bg-gray-100 transition"
            >
              <SlidersHorizontal size={24} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-full border cust-table">
              <thead>
                <tr className="border-b bg-lightBlue-500 text-white">
                  <th className="cell py-2 px-4 font-bold text-left">Type</th>
                  <th className="cell py-2 px-4 font-bold text-left">
                    Expense Description
                  </th>
                  <th className="cell py-2 px-4 font-bold text-left">
                    Amount({currency})
                  </th>
                  <th className="cell py-2 px-4 font-bold text-left">Date</th>
                  <th className="cell py-2 px-4 font-bold text-left">By</th>
                  <th className="cell py-2 px-4 font-bold text-left">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {expenseData &&
                expenseData.expenses &&
                expenseData.expenses.length > 0 ? (
                  expenseData.expenses.map((expense) => (
                    <tr
                      key={expense.expenseId}
                      className={`cust-row`}
                      style={{
                        backgroundColor:
                          expense.expenseTypeId === "Cr."
                            ? "#D5F5E3 "
                            : "#FADBD8",
                      }}
                    >
                      {/* <td className="cell py-1 px-4 text-left">{expense.expenseDescType}({expense.expenseTypeId})</td> */}
                      <td className="cell py-1 px-4 text-left">
                        {expense.expenseTypeId === "Cr." ? (
                          <i className="fas fa-arrow-up text-emerald-500 mr-1 "></i>
                        ) : (
                          <i className="fas fa-arrow-down text-red-500 mr-1"></i>
                        )}
                        {expense.expenseDescType}
                      </td>
                      <td className="cell py-1 px-4 text-left">
                        {expense.description}
                      </td>
                      <td className="cell py-1 px-4 text-left">
                        {expense.expenses}
                      </td>
                      <td className="cell py-1 px-4 text-left">
                        {formatPayDate(expense.date)}
                      </td>
                      <td className="cell py-1 px-4 text-left">
                        {expense.userName}
                      </td>
                      <td className="cell py-1 px-4 text-left">
                        {expense.balance}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center text-lg text-gray-900 mt-4"
                    >
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};
export default UserExpenses;